import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Bank, Moon, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { formatEUR } from "@/lib/api";

/**
 * Drawer Cash V3 — drill-down d'un mois (M+1 / M+2 / M+3).
 * Liste les dossiers contributeurs, triés par CA net décroissant.
 * Click sur une ligne → navigation vers /dossier/{id}.
 *
 * UI mobile-first : slide-up bottom sheet (mobile), centered modal (desktop).
 */
const ETAPE_LABEL = {
  accord_risque: "Accord risque",
  offre_editee: "Offre éditée",
  acte_authentique: "Acte signé",
};

const ETAPE_COLOR = {
  accord_risque: "var(--cf-success-text)",   // teal
  offre_editee: "var(--cf-primary)",    // blue
  acte_authentique: "var(--cf-success-text)",// emerald (déjà signé, encaissement à venir)
};

export default function CashDrillDrawer({ open, month, onClose }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open || !month) return null;

  const contributors = month.contributors || [];
  const monthFull = new Date(month.year, month.month - 1, 1)
    .toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  const open_dossier = (id) => {
    onClose?.();
    navigate(`/dossier/${id}`);
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-sm flex items-end md:items-center justify-center"
      onClick={onClose}
      data-testid="cash-drill-overlay"
    >
      <div
        className="bg-white w-full md:max-w-lg md:rounded-2xl rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="cash-drill-drawer"
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400">
                Visibilité cash · {month.month_label}
              </div>
              <div className="font-display text-xl font-extrabold text-slate-900 leading-tight capitalize mt-0.5">
                {monthFull}
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100"
              data-testid="cash-drill-close"
            >
              <X size={18} weight="bold" />
            </button>
          </div>

          <div className="flex items-baseline gap-4 mt-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">CA prévu</div>
              <div className="text-[20px] font-extrabold text-slate-900 tabular-nums">{formatEUR(month.ca)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Trésorerie nette</div>
              <div
                className="text-[20px] font-extrabold tabular-nums"
                style={{ color: month.tresorerie >= 0 ? "var(--cf-success-text)" : "var(--cf-danger-text)" }}
              >
                {formatEUR(month.tresorerie)}
              </div>
            </div>
            <div className="ml-auto text-right">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Dossiers</div>
              <div className="text-[20px] font-extrabold text-slate-900 tabular-nums">{contributors.length}</div>
            </div>
          </div>
        </div>

        {/* Liste */}
        <div className="flex-1 overflow-y-auto px-3 py-2">
          {contributors.length === 0 ? (
            <div className="text-center py-10 text-[13px] text-slate-400 font-semibold">
              Aucun dossier prévu pour ce mois.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {contributors.map((c) => {
                const etapeLabel = ETAPE_LABEL[c.etape] || c.etape;
                const etapeColor = ETAPE_COLOR[c.etape] || "var(--cf-text-2)";
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => open_dossier(c.id)}
                      className="w-full text-left px-2 py-3 flex items-center gap-3 hover:bg-slate-50 rounded-lg transition-colors"
                      data-testid={`cash-drill-row-${c.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="font-extrabold text-slate-900 text-[14px] truncate">{c.nom}</span>
                          {c.is_standby && (
                            <Moon size={12} weight="fill" color="var(--cf-accent)" className="flex-shrink-0" />
                          )}
                          {c.status === "signe" && (
                            <CheckCircle size={12} weight="fill" color="var(--cf-success)" className="flex-shrink-0" />
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-[11.5px] font-semibold text-slate-500">
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
                            style={{ background: etapeColor + "1A", color: etapeColor }}
                          >
                            {etapeLabel}
                          </span>
                          {c.banque && (
                            <span className="inline-flex items-center gap-1 text-slate-500">
                              <Bank size={11} weight="fill" /> {c.banque}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-extrabold text-slate-900 text-[14px] tabular-nums">
                          {formatEUR(c.ca_net)}
                        </div>
                        <div className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">CA net</div>
                      </div>
                      <ArrowRight size={14} weight="bold" className="text-slate-300 flex-shrink-0" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
